import React, { useContext, useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { SplitButton } from 'primereact/splitbutton';

import constants from 'constants/index';
import { useSecureMessageContext } from '../Context/SecureMessageContext';
import { UserDetailsContext } from 'context/userDetailsContext';
import useCompanionCase from 'modules/cases/hooks/useCompanionCase';
import CompanionCaseMessageModal from '../CreateMessageModal/CompanionCaseMessageModal';

function SecureMessageHeader({ setShow }) {
  const { isGlobalMessagesScreen, caseDetails, isLeadSpecific } = useSecureMessageContext();
  const { companionList } = useCompanionCase(caseDetails?.case_id);

  const userContext = useContext(UserDetailsContext);
  const hasCompanion = companionList?.length > 0 && !isLeadSpecific;
  const [showCompanionModal, setShowCompanionModal] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Detect theme changes
  useEffect(() => {
    const checkTheme = () => {
      const theme = document.documentElement.getAttribute('data-theme');
      setIsDarkMode(theme === 'dark');
    };

    // Check initial theme
    checkTheme();

    // Listen for theme changes
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="d-flex align-items-center mb-2">
      {isGlobalMessagesScreen && (
        <>
          <div className="col-6 d-flex">
            <h1 className="title gray-800 mb-0 fs-3 fs-md-1">Secure Messages</h1>
          </div>
        </>
      )}
      <div className={isGlobalMessagesScreen ? 'col-6 d-flex justify-content-end' : 'col-12 d-flex justify-content-end'}>
        <div className="col-12 d-flex justify-content-end">
          {userContext.getPermission('messages') === constants.permissions.WRITE && (
            <>
              {!hasCompanion ? (
                <Button
                  label="Create Message"
                  className="btn-outline p-button-primary"
                  icon="pi pi-plus"
                  type="button"
                  onClick={() => setShow({ companion_link: false })}
                />
              ) : (
                <SplitButton
                  label="Create Message"
                  icon="pi pi-plus"
                  onClick={() => setShow({ companion_link: false })}
                  className="p-button-primary p-0"
                  model={[
                    {
                      label: 'Create Message in All Companion Cases',
                      command: (e) => setShowCompanionModal(true),
                      template: (item, options) => (
                        <div
                          className={options.className}
                          onClick={options.onClick}
                          style={{
                            padding: '12px 16px',
                            cursor: 'pointer',
                          }}
                          role="menuitem"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              options.onClick(e);
                            }
                          }}
                        >
                          <div>
                            <div
                              style={{
                                fontWeight: '600',
                                color: isDarkMode ? '#e0e0e0' : '#333',
                                marginBottom: '4px',
                              }}
                            >
                              {item.label}
                            </div>
                            <div
                              style={{
                                fontSize: '12px',
                                color: isDarkMode ? '#9ab7fb' : '#6c757d',
                                lineHeight: '1.4',
                              }}
                            >
                              Send the same message to the primary contact in each companion case. This creates an independent message in
                              each case, not a group thread.
                            </div>
                          </div>
                        </div>
                      ),
                    },
                  ]}
                  menuStyle={{
                    width: '320px',
                    padding: '0px',
                    borderRadius: '8px',
                    boxShadow: isDarkMode ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.15)',
                    border: isDarkMode ? '1px solid #35434e' : '1px solid #e9ecef',
                    backgroundColor: isDarkMode ? '#1c2331' : '#ffffff',
                  }}
                  aria-label="Create Message Options"
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Companion Case Message Modal */}
      {!isLeadSpecific && <CompanionCaseMessageModal show={showCompanionModal} setShow={setShowCompanionModal} caseDetails={caseDetails} />}
    </div>
  );
}

export default SecureMessageHeader;
